import React, { useState, useEffect } from 'react';
import { EditButton, SaveButton, ExportButton, DeleteButton, AddButton, PaginationDropdown } from '../buttons';
import './HeaderActions.scss';

export interface ActionButton {
  /** Button label */
  label: string;
  /** Button type for styling */
  type?: 'primary' | 'secondary' | 'danger' | 'default' | 'split';
  /** Click handler */
  onClick: () => void;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Whether button should be shown */
  visible?: boolean;
  /** Icon to display */
  /** Icon to display */
  icon?: string | React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Additional props for individual components */
  componentProps?: {
    isEditing?: boolean;
    selectedItemsCount?: number;
  };
}

export interface DropdownOption {
  /** Option label */
  label: string;
  /** Option value */
  value: number;
  /** Whether option is selected */
  selected?: boolean;
}

export interface HeaderActionsProps {
  /** Array of action buttons to display */
  buttons?: ActionButton[];
  /** Dropdown configuration */
  dropdown?: {
    label: string;
    value: number;
    options: DropdownOption[];
    onChange: (value: number) => void;
  };
  /** Custom action buttons to include */
  customActions?: React.ReactNode;
  /** Whether to use individual button components instead of generic buttons */
  useIndividualComponents?: boolean;
}

/**
 * Generic header actions component for data dashboards
 * Configuration-based approach for maximum flexibility
 */
const HeaderActions: React.FC<HeaderActionsProps> = ({
  buttons = [],
  dropdown,
  customActions,
  useIndividualComponents = false
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.show-dropdown-container')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Render individual button components
  const renderIndividualButton = (button: ActionButton, index: number) => {
    const { label, onClick, disabled, visible, icon, className, componentProps } = button;

    if (visible === false) return null;

    // Map button types to individual components
    switch (button.type) {
      case 'primary':
        return <SaveButton key={index} onSave={onClick} disabled={disabled} label={label} />;
      case 'danger':
        return <DeleteButton
          key={index}
          onDelete={onClick}
          disabled={disabled}
          selectedItemsCount={componentProps?.selectedItemsCount || 0}
        />;
      default:
        // For default buttons, try to match by label
        // Match 'edit' buttons or 'cancel' only if it's specifically for edit mode toggle
        const lowerLabel = label.toLowerCase();
        const isEditModeToggle = componentProps?.isEditing !== undefined;
        if (lowerLabel.includes('edit') && isEditModeToggle) {
          // This is an edit mode toggle button (Edit/Cancel Edit)
          return <EditButton
            key={index}
            isEditing={componentProps?.isEditing || false}
            onToggle={onClick}
            disabled={disabled}
          />;
        }
        if (label.toLowerCase().includes('export')) {
          return <ExportButton
            key={index}
            onExport={onClick}
            disabled={disabled}
            selectedItemsCount={componentProps?.selectedItemsCount || 0}
            label={label}
          />;
        }
        if (label.toLowerCase().includes('add')) {
          return <AddButton key={index} onAdd={onClick} disabled={disabled} label={label} icon={icon as string} />;
        }
        // Fallback to generic button
        return (
          <button
            key={index}
            className={`action-button ${button.type ? `action-button-${button.type}` : ''} ${className || ''}`}
            onClick={onClick}
            disabled={disabled}
          >
            {icon && <span className="button-icon">{icon}</span>}
            {label}
          </button>
        );
    }
  };

  return (
    <div className="admin-v2-header-actions">
      {/* Render buttons */}
      {useIndividualComponents
        ? buttons.map((button, index) => renderIndividualButton(button, index))
        : buttons
          .filter(button => button.visible !== false)
          .map((button, index) => (
            <button
              key={index}
              className={`action-button ${button.type ? `action-button-${button.type}` : ''}`}
              onClick={button.onClick}
              disabled={button.disabled}
            >
              {button.icon && <span className="button-icon">{button.icon}</span>}
              {button.label}
            </button>
          ))
      }

      {/* Render dropdown */}
      {dropdown && (
        useIndividualComponents ? (
          <PaginationDropdown
            itemsPerPage={dropdown.value}
            onItemsPerPageChange={dropdown.onChange}
            options={dropdown.options.map(opt => opt.value)}
            label={dropdown.label}
          />
        ) : (
          <div className="show-dropdown-container">
            <button
              className="action-button show-dropdown-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
            >
              {dropdown.label} {dropdown.value}
              <span className="dropdown-arrow">▼</span>
            </button>

            {showDropdown && (
              <div
                className="show-dropdown-menu"
                onClick={(e) => e.stopPropagation()}
              >
                {dropdown.options.map(option => (
                  <button
                    key={option.value}
                    className={`dropdown-option ${option.selected ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dropdown.onChange(option.value);
                      setShowDropdown(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {/* Render custom actions */}
      {customActions}
    </div>
  );
};

export default HeaderActions;
